import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { fetchPublications } from '../../../fetchProjects';

const API_BASE_URL = 'https://spaceportfolio-api.rajansudhan0.workers.dev';

const PUBLICATION_TYPES = [
  'Journal Article', 'Conference Paper', 'Workshop Paper',
  'Preprint', 'Book Chapter', 'Thesis', 'Technical Report', 'Other'
];
const STATUSES = ['Published', 'Under Review', 'Preprint', 'In Press', 'Accepted'];

const PublicationManager = ({ adminKey }) => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPub, setEditingPub] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => { loadPublications(); }, []);

  const loadPublications = async (force = false) => {
    setLoading(true);
    const data = await fetchPublications(force, adminKey);
    setPublications(data);
    setLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setStatusMessage('');
    const fd = new FormData(e.target);
    const parseList = (s) => s ? s.split(',').map(x => x.trim()).filter(Boolean) : [];

    const body = {
      id: editingPub?.id || undefined,
      title: fd.get('title'),
      authors: fd.get('authors'),
      publicationType: fd.get('publicationType'),
      journal: fd.get('journal'),
      publisher: fd.get('publisher'),
      year: fd.get('year'),
      publishedDate: fd.get('publishedDate'),
      doi: fd.get('doi'),
      url: fd.get('url'),
      pdfUrl: fd.get('pdfUrl'),
      codeUrl: fd.get('codeUrl'),
      abstract: fd.get('abstract'),
      keywords: JSON.stringify(parseList(fd.get('keywords'))),
      status: fd.get('status'),
      citations: parseInt(fd.get('citations') || '0'),
      institution: fd.get('institution'),
      fundedBy: fd.get('fundedBy'),
      award: fd.get('award'),
      image: fd.get('image'),
      featured: fd.get('featured') === 'on' ? 1 : 0,
      display_order: parseInt(fd.get('display_order') || '0'),
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/publications`, {
        method: editingPub ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Save failed');
      setStatusMessage({ type: 'success', text: 'Publication saved!' });
      loadPublications(true);
      setTimeout(() => { setIsModalOpen(false); setStatusMessage(''); }, 1500);
    } catch (err) {
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this publication?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/publications/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey },
      });
      if (!res.ok) throw new Error('Delete failed');
      loadPublications(true);
    } catch (err) { alert(err.message); }
  };

  const openAdd = () => { setEditingPub(null); setStatusMessage(''); setIsModalOpen(true); };
  const openEdit = (p) => { setEditingPub(p); setStatusMessage(''); setIsModalOpen(true); };

  const filtered = publications.filter(p => {
    const matchSearch = p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.authors?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.journal?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === 'All' || p.publicationType === filterType;
    const matchStatus = filterStatus === 'All' || p.status === filterStatus;
    return matchSearch && matchType && matchStatus;
  });

  const statusBadgeClass = (s) => {
    switch (s) {
      case 'Published': return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'Under Review': return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case 'Preprint': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'In Press': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      default: return 'bg-white/5 text-text-secondary border-white/10';
    }
  };

  if (loading) return <div className="h-64 bg-white/5 rounded-2xl animate-pulse" />;

  return (
    <div className="space-y-6 pb-12">
      {/* Top Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-8">
        <div className="flex w-full sm:w-auto gap-3">
          <div className="relative w-full sm:w-64">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input type="text" placeholder="Search publications..." value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full bg-surface/50 border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-text-primary focus:outline-none focus:border-accent/50 transition-smooth" />
          </div>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="bg-surface/50 border border-white/10 rounded-xl py-2.5 px-4 text-text-primary focus:outline-none focus:border-accent/50 transition-smooth">
            <option value="All">All Types</option>
            {PUBLICATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="bg-surface/50 border border-white/10 rounded-xl py-2.5 px-4 text-text-primary focus:outline-none focus:border-accent/50 transition-smooth">
            <option value="All">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 bg-accent hover:bg-accent-600 text-white px-5 py-2.5 rounded-xl font-semibold transition-smooth shadow-lg">
          <Icon name="Plus" size={18} /> Add Publication
        </button>
      </div>

      {/* Table */}
      <div className="bg-surface/50 border border-white/10 rounded-2xl overflow-x-auto shadow-2xl">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 text-sm font-semibold text-text-muted">
              <th className="p-4">Title</th>
              <th className="p-4">Type</th>
              <th className="p-4">Journal / Conference</th>
              <th className="p-4">Year</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="text-text-primary text-sm divide-y divide-white/5">
            {filtered.length === 0 ? (
              <tr><td colSpan="6" className="p-8 text-center text-text-muted">No publications found.</td></tr>
            ) : filtered.map(p => (
              <tr key={p.id} className="hover:bg-white/[0.02] transition-colors group">
                <td className="p-4 font-medium max-w-xs">
                  <div className="flex items-start gap-2">
                    {p.featured === 1 && <Icon name="Star" size={14} className="text-amber-400 fill-current mt-0.5 shrink-0" />}
                    <span className="line-clamp-2">{p.title}</span>
                  </div>
                  <p className="text-text-muted text-xs mt-1 truncate">{p.authors}</p>
                </td>
                <td className="p-4">
                  <span className="bg-white/5 border border-white/10 px-2.5 py-1 rounded-md text-xs whitespace-nowrap">
                    {p.publicationType || 'N/A'}
                  </span>
                </td>
                <td className="p-4 text-text-secondary max-w-[180px] truncate">{p.journal || '—'}</td>
                <td className="p-4 text-text-secondary whitespace-nowrap">{p.year || '—'}</td>
                <td className="p-4">
                  <span className={`border px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusBadgeClass(p.status)}`}>
                    {p.status || 'Published'}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                    {p.doi && (
                      <a href={`https://doi.org/${p.doi}`} target="_blank" rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-text-secondary flex items-center justify-center transition-smooth" title="Open DOI">
                        <Icon name="ExternalLink" size={14} />
                      </a>
                    )}
                    <button onClick={() => openEdit(p)}
                      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-text-secondary hover:text-white flex items-center justify-center transition-smooth" title="Edit">
                      <Icon name="Edit2" size={14} />
                    </button>
                    <button onClick={() => handleDelete(p.id)}
                      className="w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center transition-smooth border border-transparent hover:border-red-500/30" title="Delete">
                      <Icon name="Trash2" size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 pt-20 pb-4">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => !isSaving && setIsModalOpen(false)} />
          <div className="bg-surface border border-white/10 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto relative z-10 shadow-3xl">
            <div className="sticky top-0 bg-surface/90 backdrop-blur-md px-6 py-4 border-b border-white/10 flex justify-between items-center z-20">
              <h3 className="font-heading font-bold text-xl">{editingPub ? 'Edit Publication' : 'Add New Publication'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-text-muted hover:text-white transition-smooth">
                <Icon name="X" size={24} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-8">

              {/* Core Info */}
              <div className="space-y-4">
                <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Core Information</h4>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Title *</label>
                  <input type="text" name="title" required defaultValue={editingPub?.title}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent focus:ring-1 focus:ring-accent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Authors *</label>
                  <input type="text" name="authors" required defaultValue={editingPub?.authors}
                    placeholder="e.g. Sudhan R, Sharma A, Gupta B" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent focus:ring-1 focus:ring-accent" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Publication Type *</label>
                    <select name="publicationType" required defaultValue={editingPub?.publicationType || 'Journal Article'}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent">
                      {PUBLICATION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Status</label>
                    <select name="status" defaultValue={editingPub?.status || 'Published'}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent">
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">Abstract</label>
                  <textarea name="abstract" rows={4} defaultValue={editingPub?.abstract}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent resize-none" />
                </div>
              </div>

              {/* Venue */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Venue & Publisher</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Journal / Conference Name</label>
                    <input type="text" name="journal" defaultValue={editingPub?.journal}
                      placeholder="e.g. Nature, IGARSS 2024" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Publisher</label>
                    <input type="text" name="publisher" defaultValue={editingPub?.publisher}
                      placeholder="e.g. Elsevier, IEEE, Springer" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Year</label>
                    <input type="text" name="year" defaultValue={editingPub?.year}
                      placeholder="e.g. 2024" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Published Date</label>
                    <input type="text" name="publishedDate" defaultValue={editingPub?.publishedDate}
                      placeholder="e.g. March 2024" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                </div>
              </div>

              {/* Links & Identifiers */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Links & Identifiers</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">DOI</label>
                    <input type="text" name="doi" defaultValue={editingPub?.doi}
                      placeholder="e.g. 10.1000/xyz123" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Paper URL</label>
                    <input type="url" name="url" defaultValue={editingPub?.url}
                      placeholder="https://..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">PDF URL</label>
                    <input type="url" name="pdfUrl" defaultValue={editingPub?.pdfUrl}
                      placeholder="https://..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Code / Repository URL</label>
                    <input type="url" name="codeUrl" defaultValue={editingPub?.codeUrl}
                      placeholder="https://github.com/..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                </div>
              </div>

              {/* Meta */}
              <div className="space-y-4 pt-4 border-t border-white/10">
                <h4 className="font-heading font-semibold text-accent text-sm uppercase tracking-wider">Details & Meta</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Keywords (comma-separated)</label>
                    <input type="text" name="keywords"
                      defaultValue={(() => { try { return JSON.parse(editingPub?.keywords || '[]').join(', '); } catch { return editingPub?.keywords || ''; } })()}
                      placeholder="remote sensing, AI, space..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Citations</label>
                    <input type="number" name="citations" defaultValue={editingPub?.citations || 0} min="0"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Institution / Affiliation</label>
                    <input type="text" name="institution" defaultValue={editingPub?.institution}
                      placeholder="e.g. ISRO-NARL, Sri Eshwar College" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Funded By</label>
                    <input type="text" name="fundedBy" defaultValue={editingPub?.fundedBy}
                      placeholder="e.g. DST, ISRO, Self-funded" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Award / Recognition</label>
                    <input type="text" name="award" defaultValue={editingPub?.award}
                      placeholder="e.g. Best Paper Award" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Cover Image URL</label>
                    <input type="text" name="image" defaultValue={editingPub?.image}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Display Order</label>
                    <input type="number" name="display_order" defaultValue={editingPub?.display_order || 0}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-text-primary focus:border-accent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Featured?</label>
                    <div className="h-10 flex items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" name="featured" defaultChecked={editingPub?.featured === 1} className="sr-only peer" />
                        <div className="w-11 h-6 bg-white/10 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent ring-1 ring-white/10" />
                        <span className="ml-3 text-sm font-medium text-text-secondary">Show as Featured</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-6 mt-6 border-t border-white/10 sticky bottom-0 bg-surface/90 backdrop-blur-md p-4 -mx-6 -mb-6 rounded-b-2xl z-20">
                <div className="text-sm font-medium">
                  {statusMessage && (
                    <span className={statusMessage.type === 'success' ? 'text-green-400' : 'text-red-400'}>
                      {statusMessage.text}
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setIsModalOpen(false)}
                    className="px-5 py-2.5 rounded-xl text-text-primary bg-white/5 hover:bg-white/10 transition-smooth font-medium">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSaving}
                    className="px-5 py-2.5 rounded-xl text-white bg-accent hover:bg-accent-600 disabled:opacity-50 transition-smooth font-semibold flex items-center gap-2">
                    {isSaving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</> : <><Icon name="Save" size={18} /> Save Publication</>}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicationManager;
